import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { placeBet, resetPlaceBet } from "../../redux/slices/placeBetSlice";
import { freeSpin, resetFreeSpin } from "../../redux/slices/freeSpinSlice";
import { useGameSettings } from "./slotData";
import WinResult from "./WinResult";
import { useNavigate } from "react-router-dom";
import { completeBet } from "../../redux/slices/betCompleteSlice";

const SlotMachine = forwardRef(({
  bet,
  balance,
  setBalance,
  win,
  setWin,
  turboOn,
  lineForBet,
  autoPlay,
  countAutoPlay,
  setCountAutoPlay,
  setWinLineCount,
  playFreeSpin,
  setPlayFreeSpin,
  setFreeSpinDetail,
  freeSpinAutoPlayCount,
  setFreeSpinAutoPlayCount,
  setSoundEffectFile,
  setActiveModel,
  setNotificationMsg,
  autoPlayLimit,
  stopAutoPlay
}, ref) => {

  const dispatch = useDispatch();
  const { generateOrderedSymbols, lines, reels, reelStopAudioUrl, turboReelStopAudioUrl } = useGameSettings()

  const [spinResult, setSpinResult] = useState(generateOrderedSymbols());
  const [isSpinning, setIsSpinning] = useState(false);
  const [spinningReels, setSpinningReels] = useState(Array(reels).fill(false));
  const [paylines, setPaylines] = useState(null)
  const [currentPaylineIndex, setCurrentPaylineIndex] = useState(0);
  const [showWinResult, setShowWinResult] = useState(false)
  const [payLinesComplete, setPayLinesComplete] = useState(false)
  const [startFreeSpin, setStartFreeSpin] = useState(false)
  const placeBetState = useSelector((state) => state.placeBet);
  const freeSpinState = useSelector((state) => state.freeSpin);
  const navigate = useNavigate();

  const {
    data,
    result,
    loading,
    finalResult,
    error
  } = useMemo(() => {
    if (playFreeSpin) {
      return {
        data: freeSpinState.data,
        result: freeSpinState.result,
        loading: freeSpinState.loading,
        error: freeSpinState.error,
        finalResult: freeSpinState.finalResult,
      };
    } else {
      return {
        data: placeBetState.data,
        result: placeBetState.result,
        loading: placeBetState.loading,
        error: placeBetState.error,
        finalResult: null,
      };
    }
  }, [playFreeSpin, placeBetState, freeSpinState]);

  const isSpinningRef = useRef(false);

  useEffect(() => {
    if (data?.spinWheel && data?.spinWheel?.length > 0) {
      setSpinResult(data?.spinWheel)
    }
  }, [data?.spinWheel])

  const handleSpin = useCallback(() => {
    if (isSpinningRef.current) return;
    setPaylines(null)
    setNotificationMsg("")
    dispatch(resetPlaceBet())
    dispatch(resetFreeSpin())
    if (balance < bet) {
      setNotificationMsg("Insufficient balance!")
      setActiveModel(null)
      return;
    }
    isSpinningRef.current = true;
    setIsSpinning(true); // Prevent multiple spins at once
    setWin(0)
    setWinLineCount(0)
    const betData = {
      betAmount: bet,
      line: lineForBet
    };

    if (playFreeSpin) {
      dispatch(freeSpin())
    } else {
      dispatch(placeBet(betData)); // Dispatch Redux action to place bet
    }

    const spinResultCopy = generateOrderedSymbols(); // Generate random placeholder symbols
    setSpinResult(spinResultCopy); // Set initial spinning effect

    const startReelSpin = (index) => {
      setSpinningReels((prev) => {
        const newState = [...prev];
        newState[index] = true;
        return newState;
      });
    };

    for (let i = 0; i < reels; i++) {
      setTimeout(() => startReelSpin(i), i * 100);
    }

  }, [balance, bet, dispatch, generateOrderedSymbols, lineForBet, playFreeSpin, reels, setActiveModel, setNotificationMsg, setWin, setWinLineCount])

  useImperativeHandle(ref, () => ({
    handleSpin: handleSpin,
  }));

  const frozenSpinWheelRef = useRef(null); // to freeze spin result once per spin

  // Stop reels and update spinResult with frozen data
  const stopReelSpin = useCallback(
    (index) => {
      return new Promise((resolve) => {
        // Set state to stop this reel
        setSpinningReels((prev) => {
          const newState = [...prev];
          newState[index] = false;
          return newState;
        });

        const frozenSpinWheel = frozenSpinWheelRef.current;
        if (frozenSpinWheel) {
          setSpinResult((prev) =>
            prev.map((row, rowIndex) =>
              row.map((cell, colIndex) =>
                colIndex === index
                  ? { ...frozenSpinWheel[rowIndex][colIndex] }
                  : cell
              )
            )
          );
        }

        if (isSpinning) {
          setSoundEffectFile(`${turboOn ? turboReelStopAudioUrl : reelStopAudioUrl}?t=${Date.now()}`);
        }

        // Simulate visual delay, then resolve
        setTimeout(() => {
          if (index === reels - 1) {
            setTimeout(() => {
              isSpinningRef.current = false
              setIsSpinning(false);
              setShowWinResult(true);
              setTimeout(() => setShowWinResult(false), turboOn ? 5000 : 10000);
            }, 500);
          }
          resolve();
        }, turboOn ? 500 : 1000); // simulate reel stop duration
      });
    },
    [
      isSpinning,
      reelStopAudioUrl,
      reels,
      setSoundEffectFile,
      turboOn,
      turboReelStopAudioUrl,
    ]
  );

  useEffect(() => {
    if (!loading && error && isSpinning) {
      frozenSpinWheelRef.current = spinResult; // Freeze data
      const stopReelsInOrder = async () => {
        for (let i = 0; i < reels; i++) {
          await stopReelSpin(i);
        }
      };
      setTimeout(() => {
        stopReelsInOrder();
      }, turboOn ? 400 : 1500)
      setActiveModel(null)
      if (error === "No response from server") {
        navigate('/unauthorized')
      }
      setNotificationMsg(error)
      setActiveModel(null)
      if (playFreeSpin) {
        setPlayFreeSpin(false)
        setFreeSpinAutoPlayCount(0)
        setFreeSpinDetail({
          type: "",
          featureWin: 0,
          totalProfit: 0,
          gameWin: 0,
          jackPotWin: 0,
          spinCount: 0,
          spinLimit: 0,
          showFreeSpin: false
        })
      }
    }
  }, [error, isSpinning, loading, navigate, playFreeSpin, reels, setActiveModel, setFreeSpinAutoPlayCount, setFreeSpinDetail, setNotificationMsg, setPlayFreeSpin, spinResult, stopReelSpin, turboOn])

  useEffect(() => {
    if (!loading && data?.spinWheel?.length && isSpinning) {
      frozenSpinWheelRef.current = data?.spinWheel; // Freeze data
      if (typeof data?.balance === 'number' && data.balance >= 0) {
        setBalance(data.balance);
      }
      const stopReelsInOrder = async () => {
        for (let i = 0; i < reels; i++) {
          // console.log(`Stopping reel ${i}`);
          await stopReelSpin(i);
          // console.log(`Stopped reel ${i}`);
        }
      };
      setTimeout(() => {
        stopReelsInOrder();
      }, turboOn ? 400 : 1500)
    }
  }, [loading, data?.spinWheel, stopReelSpin, reels, turboOn, data?.balance, setBalance, isSpinning]);

  // useEffect to store result values like win, balance, paylines & winLineCount.
  useEffect(() => {
    if (!result || isSpinning) return;

    if (result?.isWin) {
      if (result?.balance && result?.balance > 0) {
        setBalance(result?.balance);
      }
      setShowWinResult(true);
      setTimeout(() => {
        setShowWinResult(false);
        setActiveModel(null);
      }, 3000);
    } else {
      setActiveModel(null);
    }
  }, [result, setActiveModel, setBalance, isSpinning]);

  useEffect(() => {
    if (!result || isSpinning) return;

    if (result?.balance && result?.balance > 0) {
      setBalance(result?.balance);
    }

    if (result.isWin) {
      setWinLineCount(result.winLines.length);
      setWin(result.winAmount);
      setPaylines(result.winLines);
    } else {
      setWin(0)
      setWinLineCount(0)
    }
  }, [result, isSpinning, setBalance, setWinLineCount, setWin]);

  // useEffect show wonFreeSpin when we get isFreeSpin true in result.
  useEffect(() => {
    if (!result || isSpinning || !result.isFreeSpin) return;
    if (startFreeSpin) {
      setPlayFreeSpin(true);
      setStartFreeSpin(false)
      setFreeSpinDetail(prev => {
        if (
          prev.spinLimit === result.spinLimit &&
          prev.type === "WonFreeSpin"
        ) return prev;

        return {
          ...prev,
          type: "WonFreeSpin",
          spinCount: result?.spinCount,
          spinLimit: result?.spinLimit,
          showFreeSpin: true
        };
      });
    }

  }, [result, isSpinning, setPlayFreeSpin, setFreeSpinDetail, startFreeSpin, payLinesComplete]);

  // useEffect for showing featureFreeSpin screen to show win amount if we have a win.
  useEffect(() => {
    if (!result || isSpinning || !playFreeSpin || !result?.isWin) return;

    setFreeSpinDetail(prev => {
      if (
        prev.spinCount === result.spinCount &&
        prev.featureWin === result.winAmount &&
        prev.type === "FeatureFreeSpin"
      ) return prev;

      return {
        ...prev,
        type: "FeatureFreeSpin",
        showFreeSpin: true,
        featureWin: result.winAmount,
        spinCount: result.spinCount,
      };
    });
  }, [result, isSpinning, playFreeSpin, setFreeSpinDetail]);

  // useEffect to store spinCount as it updates
  useEffect(() => {
    if (!result || isSpinning || !playFreeSpin || result.spinCount === undefined) return;

    setFreeSpinDetail(prev => {
      if (prev.spinCount === result.spinCount) return prev;

      return {
        ...prev,
        spinCount: result.spinCount
      };
    });
  }, [result, isSpinning, playFreeSpin, setFreeSpinDetail]);

  // complete free spin feature and show totalFreeSpin screen
  useEffect(() => {
    if (!playFreeSpin) return;
    if (finalResult && !isSpinning) {
      setFreeSpinDetail(prev => {
        if (
          prev.type === "TotalFreeSpin" &&
          prev.featureWin === finalResult?.featureWin &&
          prev.totalProfit === finalResult?.totalWin &&
          prev.gameWin === finalResult?.gameWin
        ) return prev;

        return {
          ...prev,
          type: "TotalFreeSpin",
          showFreeSpin: true,
          featureWin: finalResult?.featureWin || 0,
          totalProfit: finalResult?.totalWin || 0,
          gameWin: finalResult?.gameWin || 0
        };
      });
    }
  }, [finalResult, isSpinning, playFreeSpin, setFreeSpinDetail]);

  // useEffect to show jackpot win screen 
  useEffect(() => {
    if (result?.isJackpotWin && !isSpinning && result?.jackpotWinAmount > 0) {
      setFreeSpinDetail(prev => {
        if (
          prev.type === "JackPotWin" &&
          prev.jackPotWin === result.jackpotWinAmount
        ) {
          return prev; // prevent redundant update
        }

        return {
          ...prev,
          type: "JackPotWin",
          showFreeSpin: true,
          jackPotWin: result.jackpotWinAmount
        };
      });
    }
  }, [isSpinning, result, setFreeSpinDetail]);

  // useEffect to stop every reel one by one after some interval
  useEffect(() => {
    if (!paylines || paylines.length === 0 || showWinResult) return;

    setCurrentPaylineIndex(0); // Start from index 0
    let loopCount = 0; // Track how many times we've looped

    const interval = setInterval(() => {
      setCurrentPaylineIndex(prevIndex => {
        const nextIndex = (prevIndex + 1) % paylines.length;

        // If we complete one full cycle & autoPlay is true, mark payLinesComplete
        if (nextIndex === 0) {
          loopCount++;
          if (loopCount === 1 && autoPlay) {
            setPayLinesComplete(true);
          }
          if (loopCount === 1 && result?.isFreeSpin) {
            setStartFreeSpin(true)
          }
        }

        return nextIndex;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [autoPlay, paylines, result?.isFreeSpin, showWinResult]);

  // space for spin useEffect
  // useEffect(() => {
  //   const handleKeyPress = (event) => {
  //     if (event.code === "Space" && spaceForSpin) {
  //       event.preventDefault(); // Prevents page scrolling when Spacebar is pressed
  //       handleSpin();
  //     }
  //   };

  //   window.addEventListener("keydown", handleKeyPress);

  //   return () => {
  //     window.removeEventListener("keydown", handleKeyPress);
  //   };
  // }, [handleSpin, spaceForSpin]);

  // spin automatically when we have auto play turned on
  useEffect(() => {
    if (
      autoPlay &&
      !isSpinning &&
      (!result?.isWin || (result?.isWin && payLinesComplete))
    ) {
      if (result?.isWin) {
        setPayLinesComplete(false);
      }
      if (autoPlayLimit.winLimit < balance || autoPlayLimit.lossLimit > balance - bet) {
        stopAutoPlay()
        setNotificationMsg("Auto Spin limit exceeded, so auto spin has been turned off!")
        return
      }
      if (countAutoPlay !== -1 && !playFreeSpin) {
        setCountAutoPlay(countAutoPlay - 1);
      }
      handleSpin();
    }
  }, [autoPlay, countAutoPlay, isSpinning, payLinesComplete, result, handleSpin, setCountAutoPlay, playFreeSpin, autoPlayLimit.winLimit, autoPlayLimit.lossLimit, balance, bet, stopAutoPlay, setNotificationMsg]);

  // spin automatically if we have free spin bonus
  useEffect(() => {
    if (playFreeSpin && !isSpinning && (!result?.isWin || (result?.isWin && payLinesComplete))) {
      if (result?.isWin) {
        setPayLinesComplete(false)
      };
      handleSpin()
      setFreeSpinAutoPlayCount(freeSpinAutoPlayCount - 1)
    }
  }, [freeSpinAutoPlayCount, handleSpin, isSpinning, payLinesComplete, playFreeSpin, result?.isWin, setFreeSpinAutoPlayCount])

  // Generate svg path for pay lines
  const generateSVGPath = (pattern, containerWidth, containerHeight) => {
    const reels = pattern[0].length; // Number of columns
    const lines = pattern.length; // Number of rows

    const cellWidth = containerWidth / reels;
    const cellHeight = containerHeight / lines;

    const points = [];
    let lastValidCol = -1;
    let allRowsHaveOne = true; // Check if all rows contain at least one `1`

    // Determine the last column where we found at least one `1`
    for (let col = 0; col < reels; col++) {
      let colHasOne = false;
      for (let row = 0; row < lines; row++) {
        if (pattern[row][col] === 1) {
          lastValidCol = col;
          colHasOne = true;
        }
      }
      if (!colHasOne) {
        allRowsHaveOne = false;
      }
    }

    // Find the row with the most continuous `1`s (fully filled row)
    let selectedRow = -1;
    for (let row = 0; row < lines; row++) {
      if (pattern[row].every(cell => cell === 1)) {
        selectedRow = row;
        break;
      }
    }

    if (selectedRow !== -1) {
      // Full row of 1s, extend fully to the right
      for (let col = 0; col < reels; col++) {
        const x = col * cellWidth + cellWidth / 2;
        const y = selectedRow * cellHeight + cellHeight / 2;
        points.push({ x, y });
      }
    } else {
      // Build path only up to the last valid column
      for (let col = 0; col <= lastValidCol; col++) {
        for (let row = 0; row < lines; row++) {
          if (pattern[row][col] === 1) {
            const x = col * cellWidth + cellWidth / 2;
            const y = row * cellHeight + cellHeight / 2;
            points.push({ x, y });
            break; // Move to the next column
          }
        }
      }
    }

    if (points.length === 0) return "";

    // Extend to the left outside
    const firstPoint = points[0];
    points.unshift({ x: firstPoint.x - cellWidth, y: firstPoint.y });

    // Extend to the right **only if all rows have at least one `1`**
    if (selectedRow !== -1 || allRowsHaveOne) {
      const lastPoint = points[points.length - 1];
      points.push({ x: lastPoint.x + cellWidth, y: lastPoint.y });
    }

    // Convert to SVG path
    return `M ${points.map(p => `${p.x},${p.y}`).join(" L ")}`;
  };

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
        setContainerHeight(containerRef.current.offsetHeight);
      }
    };

    updateSize(); // Initial set
    window.addEventListener('resize', updateSize); // On resize

    return () => window.removeEventListener('resize', updateSize); // Cleanup
  }, []);

  useEffect(() => {
    if (result?.status && !isSpinning) {
      dispatch(completeBet());
    }
  }, [dispatch, isSpinning, result?.status])

  return (
    <div
      className="xs:w-[62.5%] xs:h-[69%] xs:top-[1.5%] 
                 lg:w-[62.5%] lg:h-[68.7%] lg:top-[1.5%]   
                 xl:w-[62.5%] xl:h-[70.4%] xl:top-[2.9%] 
                 2xl:w-[62.5%] 2xl:h-[70.3%] 2xl:top-[2.5%] 
                 3xl:w-[62.5%] 3xl:h-[69.5%] 3xl:top-[2.3%] 
                 bg-center bg-no-repeat flex items-center justify-center absolute inset-0 m-auto"
      style={{
        backgroundImage: "url('/slot-machine-reel-bg.png')",
        backgroundSize: "100% 100%",
      }}
    >
      {showWinResult && paylines && (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            position: "absolute",
            zIndex: 50
          }}
        >
          <WinResult winAmount={win} setSoundEffectFile={setSoundEffectFile} />
        </div>
      )}
      <div className="w-full h-full flex items-center justify-center">
        <div ref={containerRef} className="w-[94%] h-[96%] flex items-center justify-center overflow-hidden">
          {/* Grid layout for slot machine */}
          <div
            className="grid w-full h-full rounded-md relative"
            style={{
              gridTemplateColumns: `repeat(${reels}, 1fr)`,
              gridTemplateRows: `repeat(${lines}, 1fr)`,
            }}
          >
            {
              (() => {
                const highlightedCells = new Set();

                if (
                  paylines !== null &&
                  paylines[currentPaylineIndex] &&
                  paylines[currentPaylineIndex].pattern?.pattern &&
                  Array.isArray(spinResult) &&
                  spinResult.length > 0 &&
                  Array.isArray(spinResult[0])
                ) {
                  const originalPattern = paylines[currentPaylineIndex].pattern.pattern;
                  if (paylines[currentPaylineIndex].pattern?.isScatterWin || paylines[currentPaylineIndex].pattern?.isBonusWin) {
                    if (
                      Array.isArray(originalPattern) &&
                      originalPattern.length > 0 &&
                      Array.isArray(originalPattern[0])
                    ) {
                      for (let row = 0; row < originalPattern.length; row++) {
                        for (let col = 0; col < originalPattern[row].length; col++) {
                          if (originalPattern[row][col] === 1) {
                            highlightedCells.add(`${row}-${col}`);
                          }
                        }
                      }
                    }

                  } else {
                    if (
                      Array.isArray(originalPattern) &&
                      originalPattern.length > 0 &&
                      Array.isArray(originalPattern[0])
                    ) {
                      const currentPattern = Array.from(
                        { length: originalPattern[0].length },
                        (_, colIndex) => originalPattern.map(row => row[colIndex])
                      );

                      const spinResultTransposed = Array.from(
                        { length: spinResult[0].length },
                        (_, colIndex) => spinResult.map(row => row[colIndex])
                      );

                      const matchedSymbols = [...(paylines[currentPaylineIndex]?.matchedSymbol || [])];

                      for (let rowIndex = 0; rowIndex < spinResultTransposed.length; rowIndex++) {
                        for (let colIndex = 0; colIndex < spinResultTransposed[rowIndex].length; colIndex++) {
                          if (currentPattern?.[rowIndex]?.[colIndex] === 1 && matchedSymbols.length > 0) {
                            const symbol = spinResultTransposed[rowIndex][colIndex];
                            const matchIndex = matchedSymbols.findIndex(m => m._id === symbol._id);

                            if (matchIndex !== -1) {
                              highlightedCells.add(`${colIndex}-${rowIndex}`);
                              matchedSymbols.splice(matchIndex, 1);
                            }
                          }
                        }
                      }
                    }
                  }
                }

                return spinResult.map((row, rowIndex) =>
                  row.map((symbol, colIndex) => {
                    const isHighlighted = highlightedCells.has(`${rowIndex}-${colIndex}`);

                    return (
                      <div
                        key={`${rowIndex}-${colIndex}`}
                        className="relative flex items-center justify-center h-full w-full transition-transform duration-500"
                      >
                        {isHighlighted && !showWinResult && (
                          <div
                            className="absolute inset-0 bg-no-repeat bg-center bg-contain"
                            style={{
                              backgroundImage: "url(/highlighted.gif)",
                              backgroundSize: "114% 122%",
                              zIndex: 2,
                            }}
                          ></div>
                        )}

                        <div
                          className={`relative flex items-center justify-center h-full w-full rounded-lg bg-no-repeat bg-contain bg-center 
                          ${spinningReels[colIndex] ? "animate-scroll blur-sm" : ""}
                          ${paylines ? (isHighlighted && !showWinResult ? "animate-zoom scale-125" : "blur-sm") : ""}`}
                          style={{
                            backgroundImage: `url(${symbol.image_url})`,
                            backgroundSize: "78% 80%",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                            zIndex: isHighlighted ? 1 : 0,
                          }}
                        ></div>

                        {colIndex < reels - 1 && (
                          <div
                            className="absolute right-[-5%] top-0 bottom-0 w-[10%]"
                            style={{
                              backgroundImage: `url(/machine-separator.png)`,
                              backgroundSize: "cover",
                              backgroundRepeat: "no-repeat",
                            }}
                          ></div>
                        )}
                      </div>
                    );
                  })
                );
              })()
            }

            {!isSpinning && !showWinResult && !payLinesComplete &&
              <svg className="absolute w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
                <defs>
                  {/* Glowing Effect */}
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="3.5" result="coloredBlur" />
                    <feMerge>
                      <feMergeNode in="coloredBlur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>

                  {/* Electric Gradient */}
                  <linearGradient id="electricGradient" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="yellow" />
                    <stop offset="50%" stopColor="orange" />
                    <stop offset="100%" stopColor="red" />
                  </linearGradient>
                </defs>

                {paylines?.map((payline, index) => {
                  if (index !== currentPaylineIndex) return null;
                  if (payline.pattern?.isScatterWin || payline.pattern?.isBonusWin) return null;
                  const pathD = generateSVGPath(payline.pattern.pattern, containerWidth, containerHeight);

                  return (
                    <path
                      key={index}
                      d={pathD}
                      stroke="url(#electricGradient)"
                      strokeWidth="4"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      // filter="url(#glow)"
                      strokeDasharray="10,10"
                      strokeDashoffset="0"
                      style={{
                        animation: "electricAnim 1s linear infinite"
                      }}

                    />
                  );
                })}
              </svg>
            }

          </div>

        </div>
      </div>

      <style>{`
          @keyframes scroll {
            0% { transform: translateY(-40%); }
            100% { transform: translateY(80%); }
          }
          .animate-scroll {
            animation: scroll 0.08s linear infinite;
            }
            `}</style>
    </div>
  );
});

export default SlotMachine;